/* eslint-disable no-undef */
import React from 'react';
import ReactDOM from 'react-dom';
import { confirmDialog } from '../components/dialogs';

import lessonManager from '../managers/LessonManager';
import publisherManager from '../managers/PublisherManager';
import responseManager from '../managers/ResponseManager';
import studentActivityManager from '../managers/StudentActivityManager';
import timeManager from '../managers/TimeManager';
import toolbarManager from '../managers/ToolbarManager';
import userManager from '../managers/UserManager';

// import accessibilityService from './AccessibilityService';
// import publisherService from './PublisherService';
import StyleService from './StyleService';
import UtilsService from './UtilsService';
import highlightAnnotationService from './HighlightAnnotationService';
import lessonService from './LessonService';
import responseService from './ResponseService';
import WebReaderLanguageSelector from '../components/tools/WebReaderLanguageSelector';
import scoringService from './ScoringService';

import { Accommodations, GlobalFontSizes, LessonMode } from '../../Constants';

// import { register } from '../../i18n';

// const t = register('Core');

export class ToolbarService {
  async exitToolHandler(data) {
    const result = await confirmDialog();
    if (result.isConfirmed) {
      if (lessonManager.playerMode === LessonMode.ACTIVITY_MODE || lessonManager.playerMode === LessonMode.TEACHER_PRESENTATION) {
        await timeManager.stopAllLessonElementTimers();
        await responseService.saveAllResponses();
      }
      if (lessonManager.playerMode === LessonMode.SCORING) {
        await scoringService.updateTestItemScore(lessonManager.currentLessonElementId);
        await scoringService.submitTeacherComment(lessonManager.currentLessonElementId);
      }
      await UtilsService.exitPlayer();
    }
  }

  textHelpToolHandler = (button, data) => {
    if (publisherManager.hasTextHelpId) {
      const isOpen = !!button?.toggle;
      toolbarManager.setIsTextHelpOpen(isOpen);

      if (button.toggle) {
        if (!speechstream.isToolbarActive()) {
          // speechstream.toggleBar();
        }
      } else {
        if (speechstream.isToolbarActive()) {
          // speechstream.toggleBar();
        }
        // stop reading in progress.
        if (toolbarManager.textHelpApi.speechTools) {
          if (toolbarManager.textHelpApi.speechTools.getClickToSpeakState()) {
            toolbarManager.textHelpApi.speechTools.clickToSpeak();
          }
          toolbarManager.textHelpApi.speechTools.stop();
        }
      }
      // For Speak, ensure the play button is visible
      this.showReadSpeakerPlayButton();
    } else if (publisherManager.hasWebReaderId) {
      toolbarManager.setShowWebReader(!toolbarManager.showWebReader);
      if (toolbarManager.showWebReader) {
        this.showReadSpeakerPlayButton();
      }
    }
  }

  showWebReaderLanguageSelector = (onSelect) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const cleanup = () => {
      if (container.parentNode) {
        ReactDOM.unmountComponentAtNode(container);
        container.parentNode.removeChild(container);
      }
    };

    const handleSelect = (lang) => {
      cleanup();
      onSelect(lang);
    };

    ReactDOM.render(
      <WebReaderLanguageSelector
        isOpen={true}
        onClose={cleanup}
        onSelect={handleSelect}
      />,
      container
    );
  }

  clickReadSpeakerControlOnce = async (timeoutMs = 4000, pollMs = 100) => {
    const selectors = [
      '.rsicn-arrow-down',
      '.rsicn',
      '#readspeaker_button1 .rsbtn_play',
      '.rsbtn_play'
    ];

    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        const el = selectors.map((s) => document.querySelector(s)).find(Boolean);
        if (el) {
          try {
            el.click();
          } catch (err) {
            console.warn('Failed to click ReadSpeaker control', err);
          }
          resolve(true);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(tick, pollMs);
      };
      tick();
    });
  }

  hideReadSpeakerPlayButton = () => {
    // Inject a persistent style to keep the play button hidden
    const styleId = 'hide-rs-play-btn';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = `
        #readspeaker_button1 { display: none !important; }
        .rsbtn_play { display: none !important; }
      `;
      document.head.appendChild(styleEl);
    }

    // Also hide any already-rendered button immediately
    const btn = document.getElementById('readspeaker_button1');
    if (btn) {
      btn.style.display = 'none';
    }
  }

  showReadSpeakerPlayButton = () => {
    const styleEl = document.getElementById('hide-rs-play-btn');
    if (styleEl) {
      styleEl.remove();
    }
    const btn = document.getElementById('readspeaker_button1');
    if (btn) {
      btn.style.display = '';
    }
  }

  waitForReadSpeakerUi = async (timeoutMs = 6000, pollMs = 100) => {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (window.rspkr?.ui) {
          resolve(true);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(tick, pollMs);
      };
      tick();
    });
  }

  waitForReadSpeakerTranslationFn = async (timeoutMs = 6000, pollMs = 100) => {
    const start = Date.now();
    const tick = () => {
      const translateFn =
        window.rspkr?.ui?.Tools?.Translation?.translate ||
        window.rspkr?.ui?.getTool?.('translation')?.translate;
      if (typeof translateFn === 'function') {
        return translateFn;
      }
      return null;
    };

    return new Promise((resolve) => {
      const loop = () => {
        const fn = tick();
        if (fn) {
          resolve(fn);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve(null);
          return;
        }
        setTimeout(loop, pollMs);
      };
      loop();
    });
  }

  waitForReadSpeakerDictionaryFn = async (timeoutMs = 6000, pollMs = 100) => {
    const start = Date.now();
    const tick = () => {
      const dictTool = window.rspkr?.ui?.Tools?.Dictionary || window.rspkr?.ui?.getTool?.('dictionary');
      const fn =
        dictTool?.dictionary ||
        dictTool?.lookup ||
        dictTool?.translate ||
        dictTool?.dictionaryLookup;
      if (typeof fn === 'function') {
        return fn;
      }
      return null;
    };

    return new Promise((resolve) => {
      const loop = () => {
        const fn = tick();
        if (fn) {
          resolve(fn);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve(null);
          return;
        }
        setTimeout(loop, pollMs);
      };
      loop();
    });
  }

  ensureReadSpeakerCustomerId = () => {
    // Ensure ReadSpeaker has customerid set in the correct location
    // ReadSpeaker uses rspkr.c.data.getParam("customerid") to build API URLs
    const customerId = window.rsConf?.general?.customerid;
    if (customerId && window.rspkr?.c?.data) {
      if (!window.rspkr.c.data.params) {
        window.rspkr.c.data.params = {};
      }
      window.rspkr.c.data.params.customerid = customerId;
      return true;
    }
    return false;
  }

  primeReadSpeakerOnLoad = async () => {
    if (!publisherManager.hasWebReaderId) {
      return;
    }

    if (toolbarManager.hasReadSpeakerPrimed) {
      return;
    }

    const hasUi = await this.waitForReadSpeakerUi();
    if (!hasUi) {
      return;
    }

    toolbarManager.setShowWebReader(true);
    try {
      window.rspkr?.tools?.toggler?.add();
    } catch (e) {
      console.warn('Failed to show ReadSpeaker toolbar on load:', e);
    }

    await this.clickReadSpeakerControlOnce();
    toolbarManager.setShowWebReader(false);
    toolbarManager.hasReadSpeakerPrimed = true;
  }

  translateToolHandler = (button, data) => {
    if (toolbarManager.textHelpApi) {
      // Restore the saved selection before calling translate
      // The selection may have been lost when the button was clicked
      const savedRange = toolbarManager.savedSelectionRange;
      if (savedRange) {
        try {
          // Check if the range is still valid (node still in DOM)
          if (savedRange.startContainer && savedRange.startContainer.parentNode) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            // Clone the range again in case the original was modified
            const rangeToRestore = savedRange.cloneRange();
            selection.addRange(rangeToRestore);
          }
        } catch (e) {
          // If restoring fails, the selection may have been invalidated
          console.warn('Failed to restore saved selection range:', e);
          // Clear invalid saved range
          toolbarManager.savedSelectionRange = null;
        }
      }

      // Check if there's any text selected
      const selection = window.getSelection();
      const selectedText = selection?.toString().trim();
      if (!selectedText) {
        const dialogObject = {
          title: 'No Text Selected',
          text: 'Please select text to translate.',
          showCancelButton: false,
          confirmButtonText: `${UtilsService.confirmOk()}`
        };
        confirmDialog(dialogObject);
        return;
      }

      // Use setTimeout with 0ms to ensure selection is restored in the next event loop tick
      // This gives Chrome time to process the selection restoration before translate is called
      setTimeout(() => {
        toolbarManager.textHelpApi.textTools.translate();
        // Clear the saved selection after using it to prevent stale selections
        toolbarManager.savedSelectionRange = null;
      }, 0);
    } else if (publisherManager.hasWebReaderId) {
      // Ensure customerid is set before showing controls
      this.ensureReadSpeakerCustomerId();
      
      // Show WebReader controls (do not auto-play listen)
      toolbarManager.setShowWebReader(true);
      try {
        window.rspkr?.tools?.toggler?.add();
        this.hideReadSpeakerPlayButton();
        setTimeout(() => this.hideReadSpeakerPlayButton(), 50);
      } catch (e) {
        console.warn('Failed to show ReadSpeaker toolbar:', e);
      }

      // Restore selection for translation
      const savedRange = toolbarManager.savedSelectionRange;
      if (savedRange) {
        try {
          if (savedRange.startContainer && savedRange.startContainer.parentNode) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            const rangeToRestore = savedRange.cloneRange();
            selection.addRange(rangeToRestore);
          }
        } catch (e) {
          console.warn('Failed to restore saved selection range:', e);
        }
      }

      // Check if there's any text selected
      const selection = window.getSelection();
      const selectedText = selection?.toString().trim();
      if (!selectedText) {
        const dialogObject = {
          title: 'No Text Selected',
          text: 'Please select text to translate.',
          showCancelButton: false,
          confirmButtonText: `${UtilsService.confirmOk()}`
        };
        confirmDialog(dialogObject);
        return;
      }

      setTimeout(async () => {
        try {
          if (!toolbarManager.hasReadSpeakerPrimed) {
            await this.clickReadSpeakerControlOnce();
            const maybeFn = await this.waitForReadSpeakerTranslationFn(4000, 100);
            if (maybeFn) {
              toolbarManager.hasReadSpeakerPrimed = true;
            }
          }
          const translateFn = toolbarManager.hasReadSpeakerPrimed
            ? window.rspkr?.ui?.Tools?.Translation?.translate
            : await this.waitForReadSpeakerTranslationFn(4000, 100);
          if (typeof translateFn === 'function') {
            const userLocaleId = userManager.userLocaleId?.toLowerCase() || 'en';
            const lessonLocaleId = lessonManager.publisherTranslationLocaleId?.toLowerCase() || 'en';
            // If user's locale matches the lesson's translation locale, or if user's locale is "en",
            // show the language selector so they can choose a different language
            if (userLocaleId === lessonLocaleId || userLocaleId === 'en') {
              this.showWebReaderLanguageSelector((langCode) => {
                try {
                  translateFn(langCode);
                } catch (err) {
                  console.warn('ReadSpeaker translation invocation failed with selected language, retrying without language', err);
                  translateFn();
                }
                toolbarManager.savedSelectionRange = null;
              });
            } else {
              // User's locale is different from lesson locale and not "en",
              // automatically translate to user's locale without showing selector
              try {
                translateFn(userLocaleId);
              } catch (err) {
                console.warn('ReadSpeaker translation invocation failed with user locale, retrying without language', err);
                translateFn();
              }
              toolbarManager.savedSelectionRange = null;
            }
          } else {
            console.warn('ReadSpeaker translation tool is unavailable');
            toolbarManager.savedSelectionRange = null;
          }
        } finally {
          // Reset WebReader visibility so state stays in sync
          toolbarManager.setShowWebReader(false);
          try {
            window.rspkr?.ui?.close?.();
          } catch (e) {
            // non-fatal
          }
        }
      }, 0);
    }
  }

  dictionaryToolHandler = (button, data) => {
    if (publisherManager.hasOxfordDictionaryId && lessonManager.lessonOptions.hasOxfordDictionary) {
      toolbarManager.setIsAltDictionaryOpen(!toolbarManager.isAltDictionaryOpen);
    } else {
      if (toolbarManager.textHelpApi) {
        // Restore the saved selection before calling dictionaryLookup
        // The selection may have been lost when the button was clicked
        const savedRange = toolbarManager.savedSelectionRange;
        if (savedRange) {
          try {
            // Check if the range is still valid (node still in DOM)
            if (savedRange.startContainer && savedRange.startContainer.parentNode) {
              const selection = window.getSelection();
              selection.removeAllRanges();
              // Clone the range again in case the original was modified
              const rangeToRestore = savedRange.cloneRange();
              selection.addRange(rangeToRestore);
            }
          } catch (e) {
            // If restoring fails, the selection may have been invalidated
            console.warn('Failed to restore saved selection range:', e);
          }
        }

        // Use setTimeout with 0ms to ensure selection is restored in the next event loop tick
        setTimeout(() => {
          toolbarManager.textHelpApi.textTools.dictionaryLookup();
          // Clear the saved selection after using it to prevent stale selections
          toolbarManager.savedSelectionRange = null;
        }, 0);
      } else if (publisherManager.hasWebReaderId) {
        // Ensure customerid is set before showing controls
        this.ensureReadSpeakerCustomerId();
        
        // Show WebReader controls (do not auto-play listen)
        toolbarManager.setShowWebReader(true);
        try {
          window.rspkr?.tools?.toggler?.add();
          this.hideReadSpeakerPlayButton();
          setTimeout(() => this.hideReadSpeakerPlayButton(), 50);
        } catch (e) {
          console.warn('Failed to show ReadSpeaker toolbar:', e);
        }

        // Restore selection for dictionary
        const savedRange = toolbarManager.savedSelectionRange;
        let selectedText = '';
        if (savedRange) {
          try {
            if (savedRange.startContainer && savedRange.startContainer.parentNode) {
              const selection = window.getSelection();
              selection.removeAllRanges();
              const rangeToRestore = savedRange.cloneRange();
              selection.addRange(rangeToRestore);
              selectedText = selection.toString().trim();
            }
          } catch (e) {
            console.warn('Failed to restore saved selection range:', e);
          }
        }

        setTimeout(async () => {
          try {
            if (!toolbarManager.hasReadSpeakerPrimed) {
              await this.clickReadSpeakerControlOnce();
              const maybeDict = await this.waitForReadSpeakerDictionaryFn(4000, 100);
              if (maybeDict) {
                toolbarManager.hasReadSpeakerPrimed = true;
              }
            }
            const dictTool = window.rspkr?.ui?.Tools?.Dictionary;
            const dictionaryFn =
              dictTool?.dictionary ||
              dictTool?.lookup ||
              dictTool?.translate ||
              dictTool?.dictionaryLookup ||
              (!toolbarManager.hasReadSpeakerPrimed && await this.waitForReadSpeakerDictionaryFn(4000, 100));

            if (typeof dictionaryFn === 'function') {
              // Align with Translation usage: pass language only, rely on selection already set
              try {
                dictionaryFn('en');
              } catch (err) {
                console.warn('ReadSpeaker dictionary invocation failed, retrying without language', err);
                try {
                  dictionaryFn();
                } catch (err2) {
                  console.warn('ReadSpeaker dictionary invocation failed without language', err2);
                }
              }
            } else {
              // Try initializing if available, then log available keys
              if (typeof dictTool?.init === 'function') {
                try {
                  dictTool.init();
                } catch (e) {
                  console.warn('ReadSpeaker dictionary init failed', e);
                }
              }
              const available = dictTool ? Object.keys(dictTool) : [];
              console.warn('ReadSpeaker dictionary tool is unavailable', { available, selectedText });
            }
          } finally {
            toolbarManager.savedSelectionRange = null;
            toolbarManager.setShowWebReader(false);
            try {
              window.rspkr?.ui?.close?.();
            } catch (e) {
              // non-fatal
            }
          }
        }, 0);
      }
    }
  }

  handleFirstSelectedWord = async () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();

    if (text) {
      const words = text.split(/\s+/);
      if (words.length > 0) {
        toolbarManager.setFirstSelectedWord(words[0]);
      }
    }
  }

  textHelpSettingsHandler = (button, data) => {
    speechstream.toggleSettings();
  }

  colorToolHandler = (button, data) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsColorOpen(isOpen);
  }

  stickyNotesToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    if (!isOpen) {
      toolbarManager.setIsStickyNotesOpen(isOpen);
    }
    toolbarManager.setIsStickyNotesActive(isOpen);

    if (!toolbarManager.isToolOn('HAT')) {
      if (isOpen) {
        // highlightAnnotationService.showHighlights(lessonManager.currentLessonElementId);
        highlightAnnotationService.showScopeLessonElementHighlights();
      } else {
        // highlightAnnotationService.removeElementHighlights(lessonManager.currentLessonElementId);
        highlightAnnotationService.removeScopeLessonElementHighlights();
      }
    }

    // Announcements now handled in ToolbarButton to resolve timing issues
    // // Announce tool status for accessibility
    // // Check the actual state after setting it
    // const actualState = toolbarManager.isStickyNotesActive;
    // if (actualState) {
    //   const message = t('stickyNotesToolOpened', 'Sticky notes tool opened');
    //   accessibilityService.announceMessage(message);
    // } else {
    //   const message = t('stickyNotesToolClosed', 'Sticky notes tool closed');
    //   accessibilityService.announceMessage(message);
    // }
  }

  notepadToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsNotepadOpen(isOpen);
  }

  contentNotepadToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsContentNotepadOpen(isOpen);
  }

  eliminatorToolHandler = (button, data) => {
    const isActive = !!button?.toggle;
    toolbarManager.setIsEliminatorActive(isActive);

    // Announcements now handled in ToolbarButton to resolve timing issues
    // // Announce tool status for accessibility
    // // Check the actual state after setting it
    // const actualState = toolbarManager.isEliminatorActive;
    // if (actualState) {
    //   const message = t('strikethroughToolOpened', 'Strikethrough tool opened');
    //   accessibilityService.announceMessage(message);
    // } else {
    //   const message = t('strikethroughToolClosed', 'Strikethrough tool closed');
    //   accessibilityService.announceMessage(message);
    // }
  }

  answerFeedbackToolHandler = async (button) => {
    const isActive = !!button?.toggle;
    toolbarManager.setIsToolbarAnswerFeedbackActive(isActive);

    // Now that the tool is active, when we move slides we will see the feedback. In order to see the feedback on the current slide, we need to update doFeedbackLogic with the current lessonElementId.
    const lessonElementId = lessonManager.currentLessonElementId;
    const mode = lessonManager.playerMode;

    if (!isActive) {
      // In the case where we just turned the tool off, we also need to manually reset the responseAnswer
      const state = responseManager.getLessonElementState(lessonElementId);
      const model = lessonManager.getSlideModel(lessonElementId);

      // Checking to see if we are on a multipart activity. If so, we need to loop over each child and reset the responseAnswer
      if (model && model?.isActivityPart) {
        const parentLessonElement = lessonManager.getLessonElement(model.parentActivityElementId);
        parentLessonElement?.lessonElementIds.forEach(async (id) => {
          const childModel = lessonManager.getSlideModel(id);
          const childState = responseManager.getLessonElementState(id);
          if (childState && childState.correctAnswerShowing) {
            childState.questionBehavior.resetStudentAnswer(childState, false);
            childState.turnOffAnswerShowing();
            if (childState.isSubmitted) {
              await responseManager.scoreResponse(childModel, id);
            }
          }
        });
      } else if (state && state.correctAnswerShowing) {
        state.questionBehavior.resetStudentAnswer(state, false);
        state.turnOffAnswerShowing();
        if (state.isSubmitted) {
          await responseManager.scoreResponse(model, lessonElementId);
        }
      }
    }

    responseService.loadSlide(lessonElementId, mode);
  }

  highlightAnnotationToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsHighlightAnnotationOpen(isOpen);

    if (!toolbarManager.isToolOn('StickyNotes')) {
      if (isOpen) {
        // highlightAnnotationService.showHighlights(lessonManager.currentLessonElementId);
        highlightAnnotationService.showScopeLessonElementHighlights();
      } else {
        // highlightAnnotationService.removeElementHighlights(lessonManager.currentLessonElementId);
        highlightAnnotationService.removeScopeLessonElementHighlights();
      }
    }

    // Announcements now handled in ToolbarButton to resolve timing issues
    // // Announce tool status for accessibility
    // // Check the actual state after setting it
    // const actualState = toolbarManager.isHighlightAnnotationOpen;
    // if (actualState) {
    //   const message = t('highlightToolOpened', 'Highlight tool opened');
    //   accessibilityService.announceMessage(message);
    // } else {
    //   const message = t('highlightToolClosed', 'Highlight tool closed');
    //   accessibilityService.announceMessage(message);
    // }
  }

  basicCalculatorToolHandler = (button) => {
    toolbarManager.resetCalculatorTool();
    const isOpen = !!button?.toggle;
    toolbarManager.setIsBasicCalculatorOpen(isOpen);
    toolbarManager.toggleToolOff('GraphingCalculator');
    toolbarManager.toggleToolOff('ScientificCalculator');

    // Announcements now handled in ToolbarButton to resolve timing issues
    // // Announce tool status for accessibility
    // // Check the actual state after setting it
    // const actualState = toolbarManager.isBasicCalculatorOpen;
    // if (actualState) {
    //   const message = t('basicCalculatorToolOpened', 'Basic calculator opened');
    //   accessibilityService.announceMessage(message);
    // } else {
    //   const message = t('basicCalculatorToolClosed', 'Basic calculator closed');
    //   accessibilityService.announceMessage(message);
    // }
  }

  scientificCalculatorToolHandler = (button) => {
    toolbarManager.resetCalculatorTool();

    const isOpen = !!button?.toggle;
    toolbarManager.setIsScientificCalculatorOpen(isOpen);
    toolbarManager.toggleToolOff('BasicCalculator');
    toolbarManager.toggleToolOff('GraphingCalculator');

    // Announcements now handled in ToolbarButton to resolve timing issues
    // // Announce tool status for accessibility
    // // Check the actual state after setting it
    // const actualState = toolbarManager.isScientificCalculatorOpen;
    // if (actualState) {
    //   const message = t('scientificCalculatorToolOpened', 'Scientific calculator opened');
    //   accessibilityService.announceMessage(message);
    // } else {
    //   const message = t('scientificCalculatorToolClosed', 'Scientific calculator closed');
    //   accessibilityService.announceMessage(message);
    // }
  }

  graphingCalculatorToolHandler = (button) => {
    toolbarManager.resetCalculatorTool();
    const isOpen = !!button?.toggle;
    toolbarManager.setIsGraphingCalculatorOpen(isOpen);
    toolbarManager.toggleToolOff('BasicCalculator');
    toolbarManager.toggleToolOff('ScientificCalculator');

    // Announcements now handled in ToolbarButton to resolve timing issues
    // // Announce tool status for accessibility
    // // Check the actual state after setting it
    // const actualState = toolbarManager.isGraphingCalculatorOpen;
    // if (actualState) {
    //   const message = t('graphingCalculatorToolOpened', 'Graphing calculator opened');
    //   accessibilityService.announceMessage(message);
    // } else {
    //   const message = t('graphingCalculatorToolClosed', 'Graphing calculator closed');
    //   accessibilityService.announceMessage(message);
    // }
  }

  customaryRulerToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsCustomaryRulerOpen(isOpen);

    // Announcements now handled in ToolbarButton to resolve timing issues
    // const actualState = toolbarManager.isCustomaryRulerOpen;
    // if (actualState) {
    //   accessibilityService.announceMessage(t('customaryRulerToolOpened', 'Imperial Ruler opened'));
    // } else {
    //   accessibilityService.announceMessage(t('customaryRulerToolClosed', 'Imperial Ruler closed'));
    // }
  }

  metricRulerToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsMetricRulerOpen(isOpen);

    // Announcements now handled in ToolbarButton to resolve timing issues
    // const actualState = toolbarManager.isMetricRulerOpen;
    // if (actualState) {
    //   accessibilityService.announceMessage(t('metricRulerToolOpened', 'Metric ruler opened'));
    // } else {
    //   accessibilityService.announceMessage(t('metricRulerToolClosed', 'Metric ruler closed'));
    // }
  }

  combinedRulerToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsCombinedRulerOpen(isOpen);

    // Announcements now handled in ToolbarButton to resolve timing issues
    // const actualState = toolbarManager.isCombinedRulerOpen;
    // if (actualState) {
    //   accessibilityService.announceMessage(t('combinedRulerToolOpened', 'Ruler opened'));
    // } else {
    //   accessibilityService.announceMessage(t('combinedRulerToolClosed', 'Ruler closed'));
    // }
  }

  protractorToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsProtractorOpen(isOpen);

    // Announcements now handled in ToolbarButton to resolve timing issues
    // const actualState = toolbarManager.isProtractorOpen;
    // if (actualState) {
    //   accessibilityService.announceMessage(t('protractorToolOpened', 'Protractor tool opened'));
    // } else {
    //   accessibilityService.announceMessage(t('protractorToolClosed', 'Protractor tool closed'));
    // }
  }

  graphPaperToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsGraphPaperOpen(isOpen);

    // Announcements now handled in ToolbarButton to resolve timing issues
    // const actualState = toolbarManager.isGraphPaperOpen;
    // if (actualState) {
    //   accessibilityService.announceMessage(t('graphPaperToolOpened', 'Graph paper tool opened'));
    // } else {
    //   accessibilityService.announceMessage(t('graphPaperToolClosed', 'Graph paper tool closed'));
    // }
  }

  revealToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsRevealOpen(isOpen);
  }

  guidelineToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    toolbarManager.setIsGuidelineOpen(isOpen);

    // Announcements now handled in ToolbarButton to resolve timing issues
    // const actualState = toolbarManager.isGuidelineOpen;
    // if (actualState) {
    //   accessibilityService.announceMessage(t('lineReaderToolOpened', 'Line reader tool opened'));
    // } else {
    //   accessibilityService.announceMessage(t('lineReaderToolClosed', 'Line reader tool closed'));
    // }
  }

  doButtonAction = (buttonId, lElementId = null, forceState = null) => {
    const preventOpen = this.preventToolOpen(buttonId);
    if (preventOpen) {
      return false;
    }
    const lessonElementId = (lElementId) || lessonManager.currentLessonElementId;
    toolbarManager.doButtonAction(buttonId, lessonElementId, forceState);
  }

  preventToolOpen = (buttonId) => {
    if (lessonManager.playerMode === LessonMode.ITEM_PREVIEW) {
      if (buttonId === 'TextHelp' || buttonId === 'Translate' || buttonId === 'Dictionary') {
        if (!publisherManager.textHelpId) {
          const dialogObject = {
            title: 'Item Preview Mode',
            text: 'This feature requires a subscription key.',
            showCancelButton: false,
            confirmButtonText: `${UtilsService.confirmOk()}`
          };
          confirmDialog(dialogObject);
          return true;
        }
      }
    }
    if (lessonManager.playerMode === LessonMode.PUBLISHER_PREVIEW) {
      if (buttonId === 'Logout') {
        return true;
      }
    }
    return false;
  }

  resourcesToolHandler = async (button) => {
    const isOpen = !!button?.toggle;
    if (isOpen) {
      const shouldOpen = await this.getResource(button.data, button.id);
      toolbarManager.setIsResourcesOpen(false);
      toolbarManager.setIsResourcesOpen(shouldOpen);
      if (shouldOpen) {
        // We also want to toggle off all other resource buttons
        const resourceButtonIds = Array.from(toolbarManager.buttonSettings.keys()).filter((id) => id.startsWith('Resources'));
        resourceButtonIds.forEach((id) => {
          if (id !== button.id) {
            toolbarManager.buttonSettings.get(id).toggle = false;
          }
        });
      }
    } else {
      toolbarManager.setIsResourcesOpen(false);
    }
  }

  helpToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    if (isOpen) {
      this.getHelp(button.data, button.id);
    }
    toolbarManager.setIsHelpOpen(isOpen);
  }

  instructionToolHandler = (button) => {
    const isOpen = !!button?.toggle;
    if (isOpen) {
      toolbarManager.setInstructionData(studentActivityManager.studentInstruction);
      toolbarManager.setInstructionOpenId(button.id);
    }
    toolbarManager.setIsInstructionOpen(isOpen);
  }

  toggleTools = () => {
    if (responseService.isMarkedForReview(lessonManager.currentLessonElementId)) {
      toolbarManager.toggleToolByIdValue('Bookmark', true);
    } else {
      toolbarManager.toggleToolByIdValue('Bookmark', false);
    }
  }

  bookmarkToolHandler = (buttonSetting) => {
    const { lessonElementId } = buttonSetting;
    if (lessonElementId) {
      responseService.setMarkedForReview(lessonElementId, buttonSetting.toggle);
    } else {
      buttonSetting.toggle = false;
    }
    toolbarManager.setIsBookmarkActive(buttonSetting.toggle);
  }

  smallTextToolHandler = (buttonSetting) => {
    if (!buttonSetting.toggle) {
      toolbarManager.toggleToolByIdValue('MediumText', true);
    } else {
      toolbarManager.toggleToolByIdValue('MediumText', false);
    }
    toolbarManager.toggleToolByIdValue('LargeText', false);

    if (this.textDefaultEmpty()) {
      this.setTextDefault();
    }
    if (!buttonSetting.toggle) {
      this.setToMediumFonts();
    } else {
      this.setToSmallFonts();
    }
  }

  mediumTextToolHandler = (buttonSetting) => {
    toolbarManager.toggleToolByIdValue('SmallText', false);
    toolbarManager.toggleToolByIdValue('MediumText', true);
    toolbarManager.toggleToolByIdValue('LargeText', false);
    if (this.textDefaultEmpty()) {
      this.setTextDefault();
    }
    if (!buttonSetting.toggle) {
      this.setToMediumFonts();
    } else {
      this.setToMediumFonts();
    }
  }

  largeTextToolHandler = (buttonSetting) => {
    if (!buttonSetting.toggle) {
      toolbarManager.toggleToolByIdValue('MediumText', true);
    } else {
      toolbarManager.toggleToolByIdValue('MediumText', false);
    }
    toolbarManager.toggleToolByIdValue('SmallText', false);
    if (this.textDefaultEmpty()) {
      this.setTextDefault();
    }
    if (!buttonSetting.toggle) {
      this.setToMediumFonts();
    } else {
      this.setToLargeFonts();
    }
  }

  boldTextToolHandler = (buttonSetting) => {
    const element = document.body;
    if (buttonSetting.toggle) {
      element.classList.add('allBold');
    } else {
      element.classList.remove('allBold');
    }
  }

  setTextDefault = () => {
    // eslint-disable-next-line no-unused-vars
    for (const [key, _value] of Object.entries(GlobalFontSizes)) {
      const currentSize = StyleService.getStyleVar(key);
      GlobalFontSizes[key] = parseInt(currentSize);
    }
  }

  textDefaultEmpty = () => {
    // if the fist item is 0 then we havent init the defaults yet
    const currentSizeInt = GlobalFontSizes['--theme-font-size'];
    return currentSizeInt === 0;
  }

  setToLargeFonts = () => {
    for (const [key, value] of Object.entries(GlobalFontSizes)) {
      const newSize = `${value + 2}px`;
      StyleService.setStyleVar(key, newSize);
    }

    const elementsWithFontSize = document.querySelectorAll('[style*="font-size"]');

    // Increase font size for each element that has a font-size style attribute.
    elementsWithFontSize.forEach((element) => {
      const currentSizeInt = GlobalFontSizes['--theme-font-size'];
      let currentSize = parseInt(element.style.fontSize, 10) || currentSizeInt;

      if (!element.dataset.originalFontSize) {
        element.dataset.originalFontSize = currentSize;
      }
      currentSize = parseInt(element.dataset.originalFontSize, 10);

      const newSize = currentSize + 2;
      element.style.fontSize = `${newSize}px`;
    });
  }

  setToSmallFonts = () => {
    for (const [key, value] of Object.entries(GlobalFontSizes)) {
      const newSize = `${value - 2}px`;

      StyleService.setStyleVar(key, newSize);
    }

    const elementsWithFontSize = document.querySelectorAll('[style*="font-size"]');

    // Reduce font size for each element that has a font-size style attribute.
    elementsWithFontSize.forEach((element) => {
      const currentSizeInt = GlobalFontSizes['--theme-font-size'];
      let currentSize = parseInt(element.style.fontSize, 10) || currentSizeInt;

      if (!element.dataset.originalFontSize) {
        element.dataset.originalFontSize = currentSize;
      }
      currentSize = parseInt(element.dataset.originalFontSize, 10);

      const newSize = currentSize - 2;
      element.style.fontSize = `${newSize}px`;
    });
  }

  setToMediumFonts = () => {
    for (const [key, value] of Object.entries(GlobalFontSizes)) {
      const newSize = `${value}px`;

      StyleService.setStyleVar(key, newSize);
    }

    // Set font size for each element that has a font-size style attribute back to its default font-size.
    const elementsWithFontSize = document.querySelectorAll('[style*="font-size"]');

    elementsWithFontSize.forEach((element) => {
      if (element.dataset.originalFontSize !== null) {
        const newSize = parseInt(element.dataset.originalFontSize, 10);
        element.style.fontSize = `${newSize}px`;
      }
    });
  }

  setTeacherToolVisibility = (isTeacher, toolbarOptions) => {
    // This was causing both Reveal and AnswerFeedback to show up even if only one of them was turned on.
    // Change allows for them to work independently from one another.
    // toolbarManager.buttonSettings.get('AnswerFeedback').toggle = false;
    // toolbarManager.buttonSettings.get('AnswerFeedback').visible = isTeacher;
    // toolbarManager.buttonSettings.get('Reveal').toggle = false;
    // toolbarManager.buttonSettings.get('Reveal').visible = isTeacher;

    if (!toolbarOptions) {
      return;
    }
    for (const tools of toolbarOptions) {
      if (tools.type === 'Reveal') {
        toolbarManager.buttonSettings.get('Reveal').toggle = false;
        toolbarManager.buttonSettings.get('Reveal').visible = isTeacher;
      } else if (tools.type === 'AnswerFeedback') {
        toolbarManager.buttonSettings.get('AnswerFeedback').toggle = false;
        toolbarManager.buttonSettings.get('AnswerFeedback').visible = isTeacher;
      }
    }
  }

  setStudentInstructionVisibility = (hasInstruction) => {
    const settings = toolbarManager.buttonSettings.get('Instruction');
    const parentSettings = toolbarManager.buttonGroupSettings.get(settings.parentId);
    settings.toggle = false;
    settings.visible = hasInstruction;
    // Have to make sure the group is visible as well in order to see the button
    parentSettings.visible = hasInstruction;
  }

  async initToolbar(lessonOptions, toolbarOptions) {
    toolbarManager.initButtonGroupSettings(lessonOptions, toolbarOptions);
    toolbarManager.initButtonSettings(lessonOptions, toolbarOptions);
    await this.initIndividualTools();
    toolbarManager.initButtonActions();
    this.initSelectionPreservation();
  }

  initSelectionPreservation = () => {
    // Save selection on mouseup to preserve it before button clicks clear it
    // This is needed for translate and dictionary tools that rely on text selection
    const handleMouseUp = (e) => {
      // Only skip if the click target itself is a button or form element
      // Don't exclude based on ancestors, as that's too aggressive for Safari
      const target = e.target;
      const isClickableElement = target && (
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'TEXTAREA' ||
        (target.closest && target.closest('.toolbar-button-wrapper'))
      );

      if (isClickableElement) {
        return;
      }

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        // Only save if there's actual selected text (not just a collapsed cursor)
        if (!range.collapsed && range.toString().trim().length > 0) {
          try {
            // Clone the range to preserve it
            // Safari may handle range cloning differently, so we wrap in try-catch
            toolbarManager.savedSelectionRange = range.cloneRange();
          } catch (err) {
            // If cloning fails, clear the saved range
            toolbarManager.savedSelectionRange = null;
          }
        } else {
          // Clear saved selection if it's collapsed or empty
          toolbarManager.savedSelectionRange = null;
        }
      } else {
        // Clear saved selection if no selection exists
        toolbarManager.savedSelectionRange = null;
      }
    };

    // Add listener to document to capture all mouseup events (capture phase to run first)
    document.addEventListener('mouseup', handleMouseUp, true);

    // iPad Safari: long-press uses native context menu and may not fire mouseup.
    // Cache the selection on touchend and on selectionchange without altering desktop behavior.
    const saveSelectionIfValid = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) {
          return;
        }
        const range = sel.getRangeAt(0);
        if (range.collapsed || range.toString().trim().length === 0) {
          return;
        }
        toolbarManager.savedSelectionRange = range.cloneRange();
      } catch (_e) {
        // ignore
      }
    };
    document.addEventListener('touchend', saveSelectionIfValid, true);
    document.addEventListener('selectionchange', saveSelectionIfValid, false);
  }

  resizeToFitOld = async (toolbarElem, widthIncreasing) => {
    // If width is decreasing: Removes tools from the toolbar until there is no overflow
    // TODO: If width is increasing: Adds tools to the toolbar until there is overflow then backs up one
    const subjectGroupLayout = toolbarManager.buttonLayout.find((group) => group.id === 'subjectGroup');
    // First, we find a starting place for the limit by getting the number of tools currently visible
    // eslint-disable-next-line array-callback-return
    let limit = subjectGroupLayout.children.reduce((numVisible, buttonOrGroup) => {
      let settings = null;
      if (buttonOrGroup.type === 'button') {
        settings = toolbarManager.buttonSettings.get(buttonOrGroup.id);
      } else if (buttonOrGroup.type === 'group') {
        settings = toolbarManager.buttonGroupSettings.get(buttonOrGroup.id);
      } else {
        console.warn('Something wasn\'t a button or group', buttonOrGroup);
      }

      if (settings) {
        const isVisible = settings.visible;
        return numVisible + (isVisible ? 1 : 0);
      }
    }, 0);

    const isOverflowing = () => {
      return toolbarElem.scrollWidth > toolbarElem.clientWidth;
    };
    const waitForScrollWidthChange = (originalSize) => {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (toolbarElem.scrollWidth !== originalSize) {
            clearInterval(interval);
            resolve();
          }
        }, 10);
      });
    };

    // Then we decrease by 1 until there is no overflow or until the only tool left is the ellipsis
    while (isOverflowing() && limit > 1) {
      limit--;
      const originalSize = toolbarElem.scrollWidth;
      toolbarManager.handleSubjectGroupOverflow(limit);
      // eslint-disable-next-line no-await-in-loop
      await waitForScrollWidthChange(originalSize);
    }
    // At this point either there is no overflow or there is only one tool left and there is nothing more we can do
  }

  maxLimit = null;
  annotationMaxLimit = null;
  async resizeToFit(toolbarElem) {
    // Starts at a limit of 1 and increases the number of tools in the toolbar by 1 until it overflow. Then back up one.
    const subjectGroupLayout = toolbarManager.buttonLayout.find((group) => group.id === 'subjectGroup');
    if (this.maxLimit === null) {
      // eslint-disable-next-line array-callback-return
      this.maxLimit = subjectGroupLayout.children.reduce((numVisible, buttonOrGroup) => {
        let settings = null;
        if (buttonOrGroup.type === 'button') {
          settings = toolbarManager.buttonSettings.get(buttonOrGroup.id);
        } else if (buttonOrGroup.type === 'group') {
          settings = toolbarManager.buttonGroupSettings.get(buttonOrGroup.id);
        } else {
          console.warn('Something wasn\'t a button or group', buttonOrGroup);
        }

        if (settings) {
          const isVisible = settings.visible;
          return numVisible + (isVisible ? 1 : 0);
        }
      }, 0);
    }
    let limit = 2;

    const isOverflowing = () => {
      return toolbarElem.scrollWidth > toolbarElem.clientWidth;
    };
    const waitForParentOrChildWidthChange = (initialWidths) => {
      // Waits until either the parent width or any of the children width changes
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          const currentWidths = {
            parent: toolbarElem.scrollWidth,
            children: Array.from(toolbarElem.children).map((child) => child.clientWidth)
          };
          if (currentWidths.parent !== initialWidths.parent || currentWidths.children.some((child, i) => child !== initialWidths.children[i])) {
            clearInterval(interval);
            clearTimeout(timeout);
            resolve(true);
          }
        }, 10);
        const timeout = setTimeout(() => {
          clearInterval(interval);
          resolve(false);
        }, 200);
      });
    };

    toolbarManager.handleSubjectGroupOverflow(limit);
    while (!isOverflowing()) {
      if (limit >= this.maxLimit) {
        return; // We are not overflowing and we have reached the max limit so we can simply stop
      }
      limit++;
      const initialWidths = {
        parent: toolbarElem.scrollWidth,
        children: Array.from(toolbarElem.children).map((child) => child.clientWidth)
      };
      toolbarManager.handleSubjectGroupOverflow(limit);
      // eslint-disable-next-line no-await-in-loop
      const expanded = await waitForParentOrChildWidthChange(initialWidths);
      if (!expanded) {
        // Then adding a new tool didn't do anything so we are actually done
        return;
      }
    }
    // At this point we are overflowing and we need to back up one
    limit--;
    toolbarManager.handleSubjectGroupOverflow(limit);
  }

  async resizeAnnotationGroupToFit(toolbarElem) {
    // Starts at a limit of 1 and increases the number of tools in the annotation group by 1 until it overflow. Then back up one.
    const annotationGroupLayout = toolbarManager.buttonLayout.find((group) => group.id === 'annotationGroup');
    if (this.annotationMaxLimit === null) {
      // eslint-disable-next-line array-callback-return
      this.annotationMaxLimit = annotationGroupLayout.children.reduce((numVisible, buttonOrGroup) => {
        let settings = null;
        if (buttonOrGroup.type === 'button') {
          settings = toolbarManager.buttonSettings.get(buttonOrGroup.id);
        } else if (buttonOrGroup.type === 'group') {
          settings = toolbarManager.buttonGroupSettings.get(buttonOrGroup.id);
        } else {
          console.warn('Something wasn\'t a button or group', buttonOrGroup);
        }

        if (settings) {
          const isVisible = settings.visible;
          return numVisible + (isVisible ? 1 : 0);
        }
      }, 0);
    }
    let limit = 2;

    const isOverflowing = () => {
      return toolbarElem.scrollWidth > toolbarElem.clientWidth;
    };
    const waitForParentOrChildWidthChange = (initialWidths) => {
      // Waits until either the parent width or any of the children width changes
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          const currentWidths = {
            parent: toolbarElem.scrollWidth,
            children: Array.from(toolbarElem.children).map((child) => child.clientWidth)
          };
          if (currentWidths.parent !== initialWidths.parent || currentWidths.children.some((child, i) => child !== initialWidths.children[i])) {
            clearInterval(interval);
            clearTimeout(timeout);
            resolve(true);
          }
        }, 10);
        const timeout = setTimeout(() => {
          clearInterval(interval);
          resolve(false);
        }, 200);
      });
    };

    toolbarManager.handleAnnotationGroupOverflow(limit);
    while (!isOverflowing()) {
      if (limit >= this.annotationMaxLimit) {
        return; // We are not overflowing and we have reached the max limit so we can simply stop
      }
      limit++;
      const initialWidths = {
        parent: toolbarElem.scrollWidth,
        children: Array.from(toolbarElem.children).map((child) => child.clientWidth)
      };
      toolbarManager.handleAnnotationGroupOverflow(limit);
      // eslint-disable-next-line no-await-in-loop
      const expanded = await waitForParentOrChildWidthChange(initialWidths);
      if (!expanded) {
        // Then adding a new tool didn't do anything so we are actually done
        return;
      }
    }
    // At this point we are overflowing and we need to back up one
    limit--;
    toolbarManager.handleAnnotationGroupOverflow(limit);
  }

  async getResource({ contentItemId, displayName, resourceType, className }, buttonId) {
    /*
    Sets the resource tool to have the correct data for the resource.
    If the resource is a bookmark, then a new tab is opened with a bookmark and this method returns false to indicate that the tool should not actually be opened.
    */
    const resource = await lessonService.fetchResourceItem(contentItemId);

    toolbarManager.setResourceType(resourceType);
    if (resourceType === 'bookmark_resource') {
      // toolbarManager.setResourceData({ url: pageUrl });
      window.open(resource.pageUrl, '_blank');
      return false;
    } else if (resourceType === 'article_resource') {
      const contentLength = UtilsService.stripTagsAndEntities(resource.articleHtml).length;
      const hasImage = resource.articleHtml.indexOf('<img') !== -1;
      const paragraphCount = resource.articleHtml.split('<p').length - 1;
      toolbarManager.setResourceData({ content: resource.articleHtml, className, contentLength, hasImage, paragraphCount });
    } else if (resourceType === 'pdf_resource') {
      toolbarManager.setResourceData(resource.presignedAwsUrl);
    } else if (resourceType === 'video_resource') {
      toolbarManager.setResourceData({ fileName: resource.remoteFileName, mime: resource.mime, size: { width: parseInt(resource.width), height: parseInt(resource.height) } });
    } else if (resourceType === 'image_resource') {
      toolbarManager.setResourceData({ fileName: resource.remoteFileName, mime: resource.mime, size: { width: parseInt(resource.width), height: parseInt(resource.height) } });
    } else if (resourceType === 'file_resource') {
      const dialogObject = {
        title: 'File Download',
        text: 'Do you wish to download this file?'
      };
      const confirm = await confirmDialog(dialogObject);

      if (confirm.isConfirmed) {
        const link = document.createElement('a');
        link.download = resource.remoteFileName;
        link.href = resource.presignedAwsUrl;
        link.click();
        link.remove();
      }
      return false;
    } else {
      throw new Error(`Unknown resource type ${resourceType}`);
    }
    toolbarManager.setResourceDisplayName(displayName);
    toolbarManager.setResourceOpenId(buttonId);
    return true;
  }

  handleContentResourceClick = async (e) => {
    /*
    This html can contain resources that should open when clicked. The most robust way to handle this is to react to all clicks on the html and afterward
    check if the click was on a resource. If it was, then open the resource using the dedicated toolbar service method.
    It is important to use the toolbar service so that the correct side effects are applied to the toolbar.
    */

    const { target } = e;
    let resourceLink = null;

    if (target && target.className && target.className.indexOf) {
      // This is for linked content and looks for specific classes and attributes
      if (target.className.indexOf('ck-cartResourceItem') !== -1 && target.className.indexOf('relatedResource') !== -1 && target.getAttribute('data-forever-id')) {
        window.parent.postMessage({ type: 'linked_element', foreverId: target.getAttribute('data-forever-id') }, toolbarManager.externalUrl);
        return;
      }

      // If the target is the 'ck-cartResourceItem' element, use the target, otherwise use the first parent that is the 'ck-cartResourceItem' element (if any).
      resourceLink = (target.className.indexOf('ck-cartResourceItem') !== -1) ? target : target.closest('.ck-cartResourceItem');
    }

    if (resourceLink) {
      e.stopPropagation();
      e.preventDefault();
    } else {
      return;
    }

    // Now we have the resource link element. It has a data-content-item-id that we can use to get the content item. Then we get the entity type id and use that to determine what gets rendered.
    const contentItemId = resourceLink.getAttribute('data-content-item-id');
    const { className } = resourceLink;

    if (!contentItemId) {
      return;
    }

    const contentItem = await lessonService.fetchContentItem(contentItemId);
    let entityTypeId = contentItem?.entityTypeId;

    if (!entityTypeId) {
      const image = resourceLink.querySelectorAll('img');
      if (image && image.length > 0) {
        entityTypeId = 'image_resource';
      } else {
        return;
      }
    }

    if (entityTypeId === 'audio_resource') {
      lessonService.showInlineAudioPlayer(resourceLink);
    } else {
      const displayName = entityTypeId === 'article_resource' ? resourceLink.innerHTML : 'Resource';
      const contentLength = resourceLink.getAttribute('data-content-length');
      await this.resourcesToolHandler({
        toggle: true,
        data: { contentItemId, resourceType: entityTypeId, displayName, className, contentLength },
        id: null
      });
    }
  }

  async getHelp({ contentItemId, displayName, resourceType }, buttonId) {
    const resource = await lessonService.fetchResourceItem(contentItemId);

    if (resourceType === 'article_resource') {
      toolbarManager.setHelpData(resource.articleHtml);
    } else if (resourceType === 'pdf_resource') {
      toolbarManager.setHelpData(resource.presignedAwsUrl);
    } else {
      throw new Error(`Unknown resource type ${resourceType}`);
    }

    toolbarManager.setResourceType(resourceType);
    toolbarManager.setHelpDisplayName(displayName);
    toolbarManager.setHelpOpenId(buttonId);
  }

  async initIndividualTools() {
    // This is required to get the relevant publisher details for tool licensing.
    // await publisherService.fetchMyPublisherOptions();
    await highlightAnnotationService.fetchTextHighlights();
  }

  async initTextHelpApi() {
    // let buttonArray = ['hover', 'play', 'pause', 'stop', 'translate', 'dictionary'];

    // if (toolbarManager.contentToolAvailability.includes('Translate')) {
    //   buttonArray = buttonArray.filter(b => b !== 'translate');
    // }
    // if (toolbarManager.contentToolAvailability.includes('Dictionary')) {
    //   buttonArray = buttonArray.filter(b => b !== 'dictionary');
    // }

    if (speechstream) {
    //  speechstream.Loader.setToolBarButtons(buttonArray);
      try {
        const toolbarApi = await speechstream.Loader.lateLoad();
        toolbarManager.setTextHelpApi(toolbarApi);
      } catch (e) {
        console.error(e);
        lessonManager.setLoaded(-1);
      }
    }
  }

  getPersistence = (lessonElementId) => {
    return toolbarManager.getPersistence(lessonElementId);
  }

  getButtonSettings = (buttonId) => {
    return toolbarManager.getButtonSettings(buttonId);
  }

  buildToolPersistence(lessonElementId) {
    const omissionList = ['HAT', 'StickyNotes'];

    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    if (!lessonElementState) {
      return;
    }
    const { toolPersistence } = lessonElementState;
    if (!toolPersistence) {
      return;
    }

    toolbarManager.contentToolAvailability.forEach((buttonId) => {
      const button = toolbarManager.getButtonSettings(buttonId);
      const oldTool = toolPersistence[buttonId];

      if (button && !omissionList.includes(button.id)) {
        let tool = {
          id: buttonId,
          isActive: button.toggle,
          ...button
        };
        if (oldTool) {
          tool = { ...oldTool, ...tool };
        }
        toolPersistence[buttonId] = tool;
      }
    });
  }

  loadToolPersistence = (lessonElementId, lessonElementState) => {
    if (!lessonElementState) {
      return;
    }
    const { toolPersistence } = lessonElementState;
    if (!toolPersistence) {
      return;
    }

    const that = this;

    toolbarManager.contentToolAvailability.forEach((buttonId) => {
      if (buttonId !== 'Logout' &&
        buttonId !== 'Translate' &&
        buttonId !== 'Dictionary' &&
        buttonId !== 'Audio' &&
        buttonId !== 'TranslateSettings' &&
        buttonId !== 'TextHelp' &&
        buttonId !== 'WritingChecklist' &&
        buttonId !== 'Notepad' &&
        buttonId !== 'ContentNotepad') {
        const tool = toolPersistence[buttonId];
        if (tool) {
          that.doButtonAction(buttonId, lessonElementId, tool.isActive);
        }
      }
    });
  }

  async processAccommodations() {
    let excludeBasicCalc = false;
    let excludeScientificCalc = false;
    let excludeGraphingCalc = false;
    let excludeTextToSpeech = false;
    let excludeTranslation = false;
    let excludeDictionary = false;

    // console.debug(toolbarManager.userAccommodationsOff);

    // check user accommodations first
    if (lessonManager.isLesson) {
      excludeBasicCalc = toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_BASIC_CALCULATOR);
      excludeScientificCalc = toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_SCIENTIFIC_CALCULATOR);
      excludeGraphingCalc = toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_GRAPHING_CALCULATOR);
      excludeTextToSpeech = toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_TEXT_SPEECH);
      excludeTranslation = toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_TRANSLATION);
      excludeDictionary = toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_DICTIONARY);
    } else {
      excludeBasicCalc = toolbarManager.userAccommodationsOff.includes(Accommodations.ASSESSMENT_BASIC_CALCULATOR);
      excludeScientificCalc = toolbarManager.userAccommodationsOff.includes(Accommodations.ASSESSMENT_SCIENTIFIC_CALCULATOR);
      excludeGraphingCalc = toolbarManager.userAccommodationsOff.includes(Accommodations.ASSESSMENT_GRAPHING_CALCULATOR);
      excludeTextToSpeech = toolbarManager.userAccommodationsOff.includes(Accommodations.ASSESSMENT_TEXT_SPEECH);
      excludeTranslation = toolbarManager.userAccommodationsOff.includes(Accommodations.ASSESSMENT_TRANSLATION);
    }

    // if there is activity accommodations, do those checks.

    if (excludeBasicCalc) {
      toolbarManager.addExcludedButtonType('BasicCalculator');
    }

    if (excludeScientificCalc) {
      toolbarManager.addExcludedButtonType('ScientificCalculator');
    }

    if (excludeGraphingCalc) {
      toolbarManager.addExcludedButtonType('GraphingCalculator');
    }

    if (excludeTextToSpeech) {
      toolbarManager.addExcludedButtonType('TextHelp');
    }

    if (excludeDictionary) {
      toolbarManager.addExcludedButtonType('Dictionary');
    }

    if (excludeTranslation) {
      toolbarManager.addExcludedButtonType('Translate');
    }

    // if (!await this.hasBasicCalculatorAccess()) {
    //   toolbarManager.addExcludedButtonType('BasicCalculator');
    // }

    // if (!await this.hasGraphingCalculatorAccess()) {
    //   toolbarManager.addExcludedButtonType('GraphingCalculator');
    // }

    // if (!await this.hasScientificCalculatorAccess()) {
    //   toolbarManager.addExcludedButtonType('ScientificCalculator');
    // }
  }

  async processCaseSpecificToolExclusions() {
    if (userManager.isStudent ||
        lessonManager.playerMode === LessonMode.STUDENT_PREVIEW ||
        lessonManager.playerMode === LessonMode.SCORING ||
        // teacher present mode
        ((lessonManager.playerMode === LessonMode.ACTIVITY_MODE || lessonManager.playerMode === LessonMode.TEACHER_PRESENTATION) && userManager.isTeacher)) {
      toolbarManager.addExcludedButtonType('ContentNotepad');
    }

    if (publisherManager.hasWebReaderId) {
      // WebReader does support these tools, but they are not available in the toolbar.
      // toolbarManager.addExcludedButtonType('Dictionary');
      // toolbarManager.addExcludedButtonType('Translate');
    }
  }

  readSpeakerIgnoreRead = () => {
    if (toolbarManager.textHelpApi) {
      const elems = document.querySelectorAll('[id^="DndLiveRegion-"]');
      if (elems && elems.length > 0) {
        for (const el of elems) {
          el.setAttribute('data-ignore', '1');
        }
      }
    }
  }

  // async hasBasicCalculatorAccess() {

  //   if (lessonManager.lesson.entityType === 'lesson') {
  //     if (toolbarManager.activityAccommodationsOff.includes(Accommodations.LESSON_BASIC_CALCULATOR) &&
  //     toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_BASIC_CALCULATOR)) {
  //       return false;
  //     }
  //   } else {
  //     if (toolbarManager.activityAccommodationsOff.includes(Accommodations.ASSESSMENT_BASIC_CALCULATOR) &&
  //     toolbarManager.userAccommodationsOff.includes(Accommodations.ASSESSMENT_BASIC_CALCULATOR)) {
  //       return false;
  //     }
  //   }
  //   return true;
  // }

  // async hasScientificCalculatorAccess() {
  //   if (toolbarManager.activityAccommodationsOff.includes(Accommodations.LESSON_SCIENTIFIC_CALCULATOR) &&
  //   toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_SCIENTIFIC_CALCULATOR)) {
  //     return false;
  //   }
  //   return true;
  // }

  // async hasGraphingCalculatorAccess() {
  //   if (toolbarManager.activityAccommodationsOff.includes(Accommodations.LESSON_GRAPHING_CALCULATOR) &&
  //   toolbarManager.userAccommodationsOff.includes(Accommodations.LESSON_GRAPHING_CALCULATOR)) {
  //     return false;
  //   }
  //   return true;
  // }
}
export default new ToolbarService();
